// ─── Filter framework: the definition model (f-02062) ─────────────
// A saved or ad-hoc filter is a criteria TREE: one root group, nested groups
// (one conjunction each) and conditions. Every list that adopts the framework
// (keyed by listId, see registry.ts) speaks this one model; nothing here knows
// about Zoho or any other system.
//
// @pcp/shared is lib ES2022 only: no URL, window, Buffer, TextEncoder, btoa or
// structuredClone in this folder (the API Docker build fails with TS2304 even
// when a local tsc passes). Sizes are measured with a manual UTF-8 loop and
// copies are explicit spreads.
export const FILTER_SCHEMA_VERSION = 1;
/** Hard limits, enforced identically by web, API and MCP (validate.ts). */
export const FILTER_LIMITS = {
    /** Nesting depth INCLUDING the root group (root = 1). */
    maxDepth: 4,
    maxConditions: 50,
    maxValuesPerCondition: 100,
    /** UTF-8 bytes of the serialized definition (JSON). */
    maxSerializedBytes: 16 * 1024,
    /** Beyond this many characters an ad-hoc definition is not put in the URL
     *  (`f=`); the UI offers "Save as filter" and large definitions use the POST
     *  query route instead. */
    maxUrlChars: 3 * 1024,
    maxNameLength: 60,
    maxSearchLength: 500,
    maxStringValueLength: 500,
};
// ─── Tokens ───────────────────────────────────────────────────────
export function isFilterToken(v) {
    return !!v && typeof v === 'object' && typeof v.token === 'string';
}
export const isMeToken = (v) => isFilterToken(v) && v.token === 'me';
export const isEmptyToken = (v) => isFilterToken(v) && v.token === 'empty';
export const isTodayToken = (v) => isFilterToken(v) && v.token === 'today';
export const isRelToken = (v) => isFilterToken(v) && v.token === 'rel';
export const FILTER_ME = { token: 'me' };
export const FILTER_EMPTY = { token: 'empty' };
export const FILTER_TODAY = { token: 'today' };
export function filterRelToken(n, unit, dir = 'past') {
    return { token: 'rel', n, unit, dir };
}
// ─── Ids ──────────────────────────────────────────────────────────
// Node ids only need to be unique inside one definition (drag keys, pill keys,
// conflict diffs). A counter plus a random suffix — no crypto needed.
let idCounter = 0;
export function newFilterNodeId(prefix = 'n') {
    idCounter = (idCounter + 1) % 1_000_000;
    return `${prefix}${idCounter.toString(36)}${Math.floor(Math.random() * 0x7fffffff).toString(36)}`;
}
// ─── Constructors ─────────────────────────────────────────────────
export function makeFilterCondition(field, op, values = [], id = newFilterNodeId('c')) {
    return { kind: 'cond', id, field, op, values: [...values] };
}
export function makeFilterGroup(conj, children = [], id = newFilterNodeId('g')) {
    return { kind: 'group', id, conj, children: [...children] };
}
/** The empty definition: zero conditions, i.e. "All <items>". */
export function emptyFilterDefinition() {
    return { schema: FILTER_SCHEMA_VERSION, root: makeFilterGroup('and', [], 'root') };
}
/** A definition whose root ANDs the given conditions (the quick-filter shape). */
export function filterDefinitionOf(conditions, extra = {}) {
    const def = { schema: FILTER_SCHEMA_VERSION, root: makeFilterGroup('and', conditions, 'root') };
    if (extra.search)
        def.search = extra.search;
    if (extra.sort)
        def.sort = extra.sort;
    return def;
}
// ─── Copy ─────────────────────────────────────────────────────────
export function cloneFilterValue(v) {
    return isFilterToken(v) ? { ...v } : v;
}
export function cloneFilterNode(node) {
    if (node.kind === 'cond')
        return { ...node, values: node.values.map(cloneFilterValue) };
    return { ...node, children: node.children.map((c) => cloneFilterNode(c)) };
}
export function cloneFilterDefinition(def) {
    const out = { schema: def.schema, root: cloneFilterNode(def.root) };
    if (def.search !== undefined)
        out.search = def.search;
    if (def.sort !== undefined)
        out.sort = def.sort;
    return out;
}
// ─── Walking ──────────────────────────────────────────────────────
/** Conditions in depth-first, left-to-right order — the order the pattern
 *  numbers them 1..N (pattern.ts). */
export function listFilterConditions(root) {
    const out = [];
    const walk = (g) => {
        for (const c of g.children) {
            if (c.kind === 'cond')
                out.push(c);
            else
                walk(c);
        }
    };
    walk(root);
    return out;
}
export function countFilterConditions(root) {
    return listFilterConditions(root).length;
}
/** Depth including the root: an empty or flat root is 1. */
export function filterTreeDepth(node) {
    if (node.kind === 'cond')
        return 0;
    let max = 0;
    for (const c of node.children)
        max = Math.max(max, filterTreeDepth(c));
    return 1 + max;
}
export function findFilterNode(root, id) {
    if (root.id === id)
        return { node: root, parent: null, index: -1 };
    const walk = (g) => {
        for (let i = 0; i < g.children.length; i++) {
            const c = g.children[i];
            if (c.id === id)
                return { node: c, parent: g, index: i };
            if (c.kind === 'group') {
                const hit = walk(c);
                if (hit)
                    return hit;
            }
        }
        return null;
    };
    return walk(root);
}
/** Map every node bottom-up; return null from `fn` to drop a node. */
export function mapFilterTree(root, fn) {
    const visit = (node) => {
        if (node.kind === 'group') {
            const children = [];
            for (const c of node.children) {
                const m = visit(c);
                if (m)
                    children.push(m);
            }
            return fn({ ...node, children });
        }
        return fn({ ...node, values: [...node.values] });
    };
    const out = visit(root);
    return out && out.kind === 'group' ? out : makeFilterGroup('and', [], root.id);
}
// ─── Immutable edits (what pills, quick filters and the builder call) ─────
/** Drop empty non-root groups and collapse single-child non-root groups into
 *  their child, so removing the second-to-last member of a group turns it back
 *  into a plain condition. */
export function tidyFilterTree(root) {
    const visit = (g, isRoot) => {
        const children = [];
        for (const c of g.children) {
            if (c.kind === 'cond')
                children.push(c);
            else {
                const t = visit(c, false);
                if (t)
                    children.push(t);
            }
        }
        if (!isRoot) {
            if (children.length === 0)
                return null;
            if (children.length === 1)
                return children[0];
        }
        return { ...g, children };
    };
    return visit(root, true);
}
export function removeFilterNode(def, id) {
    if (def.root.id === id)
        return { ...def, root: makeFilterGroup(def.root.conj, [], def.root.id) };
    const root = mapFilterTree(def.root, (n) => (n.id === id ? null : n));
    return { ...def, root: tidyFilterTree(root) };
}
export function replaceFilterNode(def, id, next) {
    if (def.root.id === id) {
        return { ...def, root: next.kind === 'group' ? next : makeFilterGroup('and', [next], def.root.id) };
    }
    return { ...def, root: tidyFilterTree(mapFilterTree(def.root, (n) => (n.id === id ? next : n))) };
}
/** Append a node to a group (the root by default). */
export function addFilterNode(def, node, groupId = def.root.id, index) {
    const root = mapFilterTree(def.root, (n) => {
        if (n.kind !== 'group' || n.id !== groupId)
            return n;
        const children = [...n.children];
        const at = index === undefined ? children.length : Math.max(0, Math.min(index, children.length));
        children.splice(at, 0, node);
        return { ...n, children };
    });
    return { ...def, root };
}
/** Move a node into `groupId` at `index` (index counted after removal).
 *  Refuses to move a group into itself or a descendant. */
export function moveFilterNode(def, id, groupId, index) {
    const hit = findFilterNode(def.root, id);
    if (!hit || !hit.parent)
        return def;
    if (hit.node.kind === 'group' && findFilterNode(hit.node, groupId))
        return def;
    const without = mapFilterTree(def.root, (n) => (n.id === id ? null : n));
    const moved = addFilterNode({ ...def, root: without }, hit.node, groupId, index);
    return { ...moved, root: tidyFilterTree(moved.root) };
}
/** Set (or clear, with null) THE root-level AND condition for `field` — the
 *  contract quick filters use. Returns null when the root is OR or the field
 *  appears more than once at the root (the UI then says "Set in Advanced
 *  filter" instead of silently rewriting a structure the user built). */
export function setRootFieldCondition(def, field, next) {
    if (!canQuickEditField(def, field))
        return null;
    const idx = def.root.children.findIndex((c) => c.kind === 'cond' && c.field === field);
    const children = [...def.root.children];
    if (next === null) {
        if (idx >= 0)
            children.splice(idx, 1);
    }
    else if (idx >= 0) {
        const old = children[idx];
        children[idx] = { ...old, op: next.op, values: [...next.values] };
    }
    else {
        children.push(makeFilterCondition(field, next.op, next.values));
    }
    return { ...def, root: { ...def.root, children } };
}
/** Whether a quick filter may edit `field`: the root must AND (or hold at most
 *  one child), the field may sit at the root at most once and must not appear
 *  inside a nested group. */
export function canQuickEditField(def, field) {
    const root = def.root;
    if (root.conj === 'or' && root.children.length > 1)
        return false;
    let atRoot = 0;
    for (const c of root.children)
        if (c.kind === 'cond' && c.field === field)
            atRoot++;
    if (atRoot > 1)
        return false;
    const nested = root.children.some((c) => c.kind === 'group' && listFilterConditions(c).some((x) => x.field === field));
    return !nested;
}
/** The root-level condition a quick filter for `field` shows, if any. */
export function rootFieldCondition(def, field) {
    const c = def.root.children.find((n) => n.kind === 'cond' && n.field === field);
    return c ?? null;
}
// ─── Size ─────────────────────────────────────────────────────────
/** UTF-8 byte length without TextEncoder (not in lib ES2022). */
export function utf8ByteLength(s) {
    let n = 0;
    for (let i = 0; i < s.length; i++) {
        const c = s.charCodeAt(i);
        if (c < 0x80)
            n += 1;
        else if (c < 0x800)
            n += 2;
        else if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
            const d = s.charCodeAt(i + 1);
            if (d >= 0xdc00 && d <= 0xdfff) {
                n += 4;
                i++;
            }
            else
                n += 3;
        }
        else
            n += 3;
    }
    return n;
}
export function filterDefinitionByteSize(def) {
    return utf8ByteLength(JSON.stringify(def));
}
//# sourceMappingURL=model.js.map